import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../Navigation/root";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs } from "../../Styles";
import {  EnhancedWidget, ProgressCircle, CheckBox, CheckStyles, Slider } from "..";
import { ButtonTypes, ReportShortCut_Type } from "../../Definitions";
import { random } from "lodash";
import { Widget, AnimalInfo, Graph_QL, System } from "../../Services";
import { NewDefaultProgressCircles, NewDefaultProgressCircle, underFeedBalanceYesterday } from "../_Components";
import { AutoReport } from "../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/autoReportCall";
import NewButton from "../BaseComponents/newButton";
import { AutoReportModal } from "./Modals";
import AutoReportCard from "./SubComponents/autoReportCard ";

interface AutoReportWidgetProps {
  widget: Widget;
  animals: AnimalInfo[];
  autoReports: AutoReport[];
  system?: System;
  connect: {
    connection: Graph_QL.Connection
    domain: string
  }
}
/**
 * AutoReportWidget
 * @param header - The title displayed at the top of the widget.
 * @param systemId - The ID of the system to fetch related data.
 * @returns A React component displaying automated reports and progress circles within an enhanced widget.
 */
export default function AutoReportAndMoreWidget({ animals, widget, autoReports, system, connect }: AutoReportWidgetProps) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [addAutoReportModal, setAddAutoReportModal] = useState<boolean>(false)
  const [dirtyWidget, setDirtyWidget] = useState(widget);
  
  useEffect(() => {
    setDirtyWidget(widget);
  }, [widget])
  
  const resetWidget = () => {
    setDirtyWidget(widget);
  };

  const autoReportData = useMemo(() => {
    if (autoReports) {
      return autoReports.filter((report) => dirtyWidget.auto_reports.includes(report.auto_report_id))
    }
    return []
  }, [autoReports, dirtyWidget.auto_reports]);

  // ProgressCircles
  const defaultProgressCircles = NewDefaultProgressCircles({ animals, connect });

  const handleCheckBoxClick = (progressCircle: NewDefaultProgressCircle) => {
    if (dirtyWidget.data.includes(progressCircle.progressCircleId)) {
      setDirtyWidget({
        ...dirtyWidget,
        data: dirtyWidget.data.filter(id => id !== progressCircle.progressCircleId)
      });
    } else {
      setDirtyWidget({
        ...dirtyWidget,
        data: [...dirtyWidget.data, progressCircle.progressCircleId]
      });
    }
  }

  // #region | Left Content 
  const ProgressCirclesToShow = useMemo(() => {
    const activeData = widget.data;
  
    return defaultProgressCircles
      .filter(c => activeData.includes(c.progressCircleId))
      .map(progressCircle => (
        <ProgressCircle
          key={progressCircle.progressCircleId}
          animation
          progressed={progressCircle.progressed}
          total={progressCircle.total}
          circleType={progressCircle.circleType}
          labels={{
            header: progressCircle.header,
            unit: progressCircle.unit,
          }}
          extension={{
            hasExtension: progressCircle.tooMuchFedDisclaimer
          }}
        />
      ));
  }, [defaultProgressCircles, widget.data]);
  // #endregion

  return (
    <EnhancedWidget
      widget={dirtyWidget}
      changeData={setDirtyWidget}
      // connect={{ farm: connect.farm, domain: connect.domain, connection: connect.connection }}
      onCancel={resetWidget}
      rightContent={
        <div style={{ ...css.rightContentOuter }}>
          <div style={{ ...css.rightContentTop }}> 
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("autoReports", { defaultValue: "Auto Reports" })}</p>
          </div>
          <div style={{ margin: "0px 0px 5px 0px", ...css.rightContentSeperator }} />
          <div style={{ height: "203px", ...css.rightContent }}>
            {autoReportData.map((report) => {
              const shouldCountAnimalsFilter = report.filter.filter((filter) => filter.key === "excess_remaining")
              return <AutoReportCard
                key={report.auto_report_id}
                autoReport={report}
                onClick={() => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animals, replace: true, navArgs: { selected: system?.system_id ? { ...report, filter: [...report.filter, { key: "system_id", value: [{ id: system?.system_id, label: "Navigated from system" }] }] } : report }, currentDomain: connect.domain })}
                amount={Boolean(shouldCountAnimalsFilter.length) && shouldCountAnimalsFilter[0].value[0] ? underFeedBalanceYesterday(animals ?? [], shouldCountAnimalsFilter[0].value[0].id): undefined}
              />
          })}
          </div> 
        </div>
      }
      leftContent={<>
        {ProgressCirclesToShow.length <= 2
          ? <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 160px)", gridAutoRows: "auto", height: "100%", width: "100%", alignItems: "center", justifyContent: "center" }}>
              <>{ProgressCirclesToShow}</>
            </div>
          : <div style={{ display: "flex", height: "100%", width: "100%", alignItems: "center" }}>
              <Slider content={ProgressCirclesToShow} />
            </div>
        }
      </>}
      editMode={{
        editHeader: true,
        editLeftContent: (
          <div style={{ height: "250px", display: "flex", flexDirection: "column", gap: "10px", boxSizing: "border-box", width: "100%", overflowY: "auto", scrollbarColor: colors.greyTertiary + colors.transparent, scrollbarWidth: "thin", padding: "20px" }}>
            {defaultProgressCircles.map((p) => (
              <CheckBox
                key={p.progressCircleId + random(0, 9999)}
                text={p.header}
                isChecked={(dirtyWidget.data).includes(p.progressCircleId)}
                checkStyle={CheckStyles.grey}
                onChange={() => handleCheckBoxClick(p)}
              />
            ))}
          </div>),
        editRightContent: (
          <div style={{ ...css.rightContentOuter }}>
            <div style={{ ...css.rightContentTop }}>
              <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary }}>{t("autoReports", { defaultValue: "Auto Reports" })}</p>
            </div>
            <div style={{ margin: "0px 0px 5px 0px", ...css.rightContentSeperator }}/>
            <div style={{ height: "156px", ...css.rightContent }}>
              {autoReportData.map((report) => {
                const shouldCountAnimalsFilter = report.filter.filter((filter) => filter.key === "excess_remaining")
                return <AutoReportCard
                  key={report.auto_report_id}
                  autoReport={report}
                  onClick={() => {
                    setDirtyWidget({ ...dirtyWidget, auto_reports: dirtyWidget.auto_reports.filter((autoReportId) => autoReportId !== report.auto_report_id) });
                  }}
                  type={ReportShortCut_Type.delete}
                  amount={Boolean(shouldCountAnimalsFilter.length) && shouldCountAnimalsFilter[0].value[0] ? underFeedBalanceYesterday(animals ?? [], shouldCountAnimalsFilter[0].value[0].id) : undefined}
                />
              })}
            </div>
            <div style={{ margin: "5px 0px 0px 0px", ...css.rightContentSeperator }}/>
            <div style={{ ...css.rightContentTop, alignItems: "center" }}>
              <NewButton
                content={""}
                buttonType={ButtonTypes.glyphButton}
                onClick={() => setAddAutoReportModal(true)}
                startEnhancer={glyphs.Add({})}
              />
            </div>
            { addAutoReportModal && <AutoReportModal connection={connect.connection} autoReports={autoReports} system={system} closeModal={() => setAddAutoReportModal(false)} widget={dirtyWidget} changeData={setDirtyWidget}/> }
          </div>
        )
      }}
    />
  );
}